import {
  getProfileBirthdayValidationMessages,
  PROFILE_FIRST_NAME_VALIDATION,
} from '@/constants'
import { insertDateSlashes } from '@/helpers'
import {
  DropdownDarkDefault,
  DropdownItemDarkDefault,
  InlineButtonAdditional2,
  LoginPasswordInputBlock,
  LoginUserFirstNameInputBlock,
  LoginUsernameInputBlock,
  SignUpInputContainerSC,
  UserAgeInputBlock,
} from '@setplex/arcane'
import { useForm } from 'effector-forms'
import { type ChangeEvent } from 'react'
import { GENDERS } from '~/shared/constants/profile'
import { signUpForm } from '../model'

interface SignUpFieldsProps {
  pending: boolean
  onShowInfo: () => void
}

/**
 * Email, password and the profile fields registration now collects in one step,
 * shared by the sign-up popup and the stand-alone sign-up page (OVP-3045).
 */
SignUpFields.displayName = 'SignUpFields'
export function SignUpFields({ pending, onShowInfo }: SignUpFieldsProps) {
  const { fields, hasError, errorText } = useForm(signUpForm)

  return (
    <SignUpInputContainerSC>
      <LoginUsernameInputBlock
        name='email'
        value={fields.email.value}
        placeholder='Email*'
        label='Email*'
        disabled={pending}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          fields.email.onChange(e.target.value)
        }
        onBlur={() => fields.email.onBlur()}
        errorMessage={
          hasError('email')
            ? errorText('email', {
                required: 'Please, enter email',
                email: 'Please, enter a valid email',
                maxLength: 'Email is too long, maximum length is 50 characters',
              })
            : ''
        }
        autoComplete='off'
      />

      <LoginPasswordInputBlock
        name='password'
        value={fields.password.value}
        placeholder='Password*'
        label='Password*'
        disabled={pending}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          fields.password.onChange(e.target.value)
        }
        onBlur={() => fields.password.onBlur()}
        errorMessage={
          hasError('password')
            ? errorText('password', {
                required: 'Please, enter password',
                password:
                  'Please, enter a valid password. Only letters (aA-zZ), numbers (0-9) and symbols (@#$_&-*!?.) are allowed',
                minLength:
                  'Password is too short, minimum length is 6 characters',
                maxLength:
                  'Password is too long, maximum length is 64 characters',
              })
            : ''
        }
        autoComplete='new-password'
      />

      <LoginUserFirstNameInputBlock
        name='firstName'
        value={fields.firstName.value}
        label='First Name*'
        placeholder='First Name*'
        disabled={pending}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          fields.firstName.onChange(e.target.value)
        }
        onBlur={() => fields.firstName.onBlur()}
        errorMessage={
          hasError('firstName')
            ? errorText('firstName', PROFILE_FIRST_NAME_VALIDATION)
            : ''
        }
        autoComplete='off'
      />

      <div className='sign-up-form-inputs-wrapper'>
        <UserAgeInputBlock
          name='birthDate'
          value={fields.birthday.value}
          label='Birth date'
          placeholder='Birth date'
          maxLength='10'
          disabled={pending}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            fields.birthday.onChange(insertDateSlashes(e.target.value))
          }
          datePlaceholder='MM/DD/YYYY'
          errorMessage={
            hasError('birthday')
              ? errorText('birthday', getProfileBirthdayValidationMessages())
              : ''
          }
          autoComplete='off'
        />
        <DropdownDarkDefault title={fields.gender.value} placeholder='Gender'>
          {GENDERS.map((gender) => (
            <DropdownItemDarkDefault
              key={gender}
              title={gender}
              id={gender}
              activeId={fields.gender.value}
              onClick={() => signUpForm.fields.gender.set(gender)}
            />
          ))}
        </DropdownDarkDefault>
      </div>

      <InlineButtonAdditional2
        className='info-link'
        title='Why is this info asked?'
        onClick={onShowInfo}
      />
    </SignUpInputContainerSC>
  )
}
